import type { SiteOfCareType } from 'src/api/redux/suggestions/suggestionsApi';

export const SOC_TYPE_LABELS: Record<string, string> = {
  asc: 'ASC',
  dme_supplier: 'DME Supplier',
  endoscopy_center: 'Endoscopy Center',
  home_health: 'Home Health',
  hospital: 'Hospital',
  laboratory: 'Laboratory',
  office_clinic: 'Office / Clinic',
  pharmacy: 'Pharmacy',
  snf: 'SNF',
  urgent_care: 'Urgent Care',
};

// Hospital-system affiliation discriminator (owner_type): CORP => parent, OWNR => network.
// A null/unknown value is unspecified and renders no label.
export const OWNER_TYPE_LABELS: Record<string, string> = {
  CORP: 'Parent',
  OWNR: 'Network',
};

export const getOwnerTypeLabel = (
  ownerType?: string | null
): string | undefined => (ownerType ? OWNER_TYPE_LABELS[ownerType] : undefined);

/**
 * Title-case a snake_case ID (e.g. "acute_care" → "Acute Care").
 * Used as a fallback when the taxonomy lookup is unavailable.
 */
export const titleCaseId = (id: string): string =>
  id
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Build fast lookup maps from the SOC taxonomy array.
 * Returns maps for type id → name and subtype id → name (scoped to parent).
 */
const buildTaxonomyMaps = (
  taxonomy: SiteOfCareType[]
): { typeMap: Map<string, string>; subtypeMap: Map<string, string> } => {
  const typeMap = new Map<string, string>();
  const subtypeMap = new Map<string, string>();
  taxonomy.forEach((type) => {
    typeMap.set(type.id, type.name);
    type.subtypes?.forEach((sub) => {
      subtypeMap.set(sub.id, sub.name);
    });
  });
  return { typeMap, subtypeMap };
};

export const formatSocClassification = (
  entity?: { soc_type?: string; soc_subtype?: string } | null,
  taxonomy?: SiteOfCareType[]
): string => {
  if (!entity) return '';

  let typeLabel = '';
  let subtypeLabel = '';

  if (taxonomy?.length) {
    const { typeMap, subtypeMap } = buildTaxonomyMaps(taxonomy);
    typeLabel = entity.soc_type
      ? typeMap.get(entity.soc_type) ?? SOC_TYPE_LABELS[entity.soc_type] ?? titleCaseId(entity.soc_type)
      : '';
    subtypeLabel = entity.soc_subtype
      ? subtypeMap.get(entity.soc_subtype) ?? titleCaseId(entity.soc_subtype)
      : '';
  } else {
    typeLabel = entity.soc_type
      ? SOC_TYPE_LABELS[entity.soc_type] ?? titleCaseId(entity.soc_type)
      : '';
    subtypeLabel = entity.soc_subtype
      ? titleCaseId(entity.soc_subtype)
      : '';
  }

  return [typeLabel, subtypeLabel].filter(Boolean).join(' > ');
};
